import { styled } from "@mui/material/styles";
import { Box, Typography, Button, TextField, IconButton } from "@mui/material";
import Chip from "@mui/material/Chip";

export const ShareOverlay = styled("div")(({ theme }) => ({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1400,
  backdropFilter: "blur(4px)",
}));

export const ShareContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  maxWidth: 560,
  width: "92%",
  position: "relative",
  zIndex: 1401,
  boxShadow: theme.shadows[6],
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

export const ShareContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  gap: theme.spacing(1.25),
}));

export const ShareHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

export const ShareTitle = styled(Typography)(({ theme }) => ({
  margin: 0,
  color: theme.palette.text.primary,
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "-0.01em",
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.25,
    height: 40,
    fontSize: 14,
  },
}));

/** Chips container — collapses when no chips */
export const ChipsWrap = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasChips",
})(({ theme, hasChips }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  minHeight: hasChips ? 52 : 0,
  maxHeight: hasChips ? 180 : 0,
  overflowY: hasChips ? "auto" : "hidden",
  paddingRight: hasChips ? 4 : 0,
  paddingTop: hasChips ? 8 : 0,
  paddingBottom: hasChips ? 8 : 0,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { background: "#e0e7ef", borderRadius: 3 },
}));

export const EmptyChipsHint = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  marginTop: 2,
}));

export const PillChip = styled(Chip)(({ theme }) => ({
  borderRadius: 999,
  fontWeight: 600,
  height: 32,
  "& .MuiChip-label": { paddingLeft: 10, paddingRight: 8, fontSize: 12.5 },
  "& .MuiChip-deleteIcon": { fontSize: 18 },
}));

export const SelectAllRow = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: theme.spacing(1),
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

export const ListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: 4,
  paddingBottom: 8,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    background: "#e0e7ef",
    borderRadius: 3,
  },
}));

export const UserRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "& .text": { display: "flex", flexDirection: "column", minWidth: 0 },
  "& .email": {
    fontWeight: 700,
    lineHeight: 1.15,
    fontSize: 14.5,
    letterSpacing: 0.2,
  },
  "& .name": { opacity: 0.7, lineHeight: 1.2, fontSize: 12.5 },
}));

export const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: theme.spacing(0.75),
  borderTop: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  flexShrink: 0,
  "& .actions": { display: "flex", gap: 8 },
}));

export const ClearButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text.secondary,
}));

export const ShareButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  color: "#ffff !important",
  background: "linear-gradient(135deg, #3F49A7 0%, #2c357a 100%)",
  boxShadow: "0 2px 8px rgba(63,73,167,0.25)",
  "&:hover": {
    background: "linear-gradient(135deg, #2c357a 0%, #1e2a4a 100%)",
    boxShadow: "0 4px 12px rgba(63,73,167,0.35)",
    transform: "translateY(-1px)",
  },
}));

export const LoadingWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

export const EmptyWrap = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));
